import { createScopedErrors, registerCodes } from '@norkron/web-errors';
import { resolveLengthPx, autoUpdate as autoUpdate$1, computePosition as computePosition$1 } from '@norkron/web-floating';
import { createContext, memo, useRef, useCallback, useMemo, forwardRef, isValidElement, cloneElement, useId, useState, useEffect, useContext } from 'react';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { registerResponsiveContract, checkContrast, checkViewportOverflow, checkHeadingHierarchy, checkDuplicateVisibleText, checkTextClipped, checkInlineStyleOnLibStamp, checkUtilityClassOnLibStamp } from '@norkron/web-conformance';
import { z } from 'zod';
import { useFocusTrap, safeFocus } from '@norkron/web-a11y';
import { registerLazy } from '@norkron/web-conformance/register-lazy';
import { ErrorBoundary } from '@norkron/web-error-boundary';
import { Portal } from '@norkron/web-portal';
import { useThemeVar } from '@norkron/web-themer/react';
import { useControlledState } from '@norkron/web-controlled-state';
import { useDismiss } from '@norkron/web-dismiss';

// src/error-codes.ts
var TOOLTIP_LIB = "@norkron/web-tooltip";
var TOOLTIP_TOKEN = /* @__PURE__ */ Symbol.for("norkron.lib.tooltip");
var { warn, panic } = createScopedErrors(TOOLTIP_LIB, TOOLTIP_TOKEN);
try {
  registerCodes(TOOLTIP_LIB, {
    TOOLTIP_PORTAL_FAILURE: {
      description: "createPortal target is missing or invalid \u2014 typically `document.body` is unavailable (SSR) or `portalContainer` was passed `null`. The tooltip silently no-renders to avoid breaking the host.",
      severity: "warning",
      docsAnchor: "tooltip/portal-failure",
      exampleFix: "Pass an explicit `portalContainer` HTMLElement, or ensure `document.body` exists at render time (no SSR call to <Tooltip />)."
    },
    TT_TRIGGER_NOT_FOCUSABLE: {
      description: 'A mounted tooltip trigger (element carrying `data-norkron-lib="@norkron/web-tooltip@..."`) is not keyboard-focusable: it is neither a native focusable tag (`button`/`a`/`input`/`select`/`textarea`) nor an element with `tabindex >= 0`. Keyboard users cannot reach or trigger the tooltip \u2014 WCAG 2.1.1 Keyboard violation.',
      severity: "error",
      docsAnchor: "tooltip/trigger-not-focusable",
      exampleFix: 'Render the tooltip trigger as a native `<button>`, or add `tabindex="0"` plus an explicit `role="button"` and key handler on the custom trigger.'
    },
    TT_ACCNAME_POLLUTION: {
      description: 'A mounted tooltip trigger is a descendant of a heading element (`<h1>`..`<h6>` or `role="heading"`). The trigger contributes its accessible name to the heading\'s accname computation, polluting the heading text reported to assistive tech.',
      severity: "warning",
      docsAnchor: "tooltip/accname-pollution",
      exampleFix: "Move the tooltip trigger out of the heading element. If the trigger must sit visually near the heading, render it as a sibling of the heading, not a descendant."
    },
    TT_SELFCHECK_BUDGET_EXCEEDED: {
      description: "`selfCheck()` exceeded the configured `budgetMs` while iterating mounted triggers; the walk short-circuits and returns partial results. Informational \u2014 not a defect in the audited DOM, but a signal that the budget is too tight for the page size or the host machine is slow.",
      severity: "info",
      docsAnchor: "tooltip/selfcheck-budget-exceeded",
      exampleFix: "Increase `selfCheck(root, { budgetMs })` (default 10ms) to match the host page complexity, or audit fewer tooltips at once by scoping `root` more tightly."
    },
    TT_SELFCHECK_INTERNAL_ERROR: {
      description: "`selfCheck()` caught an unexpected throw from its internal walk. The error is surfaced as a finding instead of propagating, preserving the no-throw contract. Indicates a bug in `selfCheck` (or in a sub-API it consumes), not a defect in the audited DOM.",
      severity: "info",
      docsAnchor: "tooltip/selfcheck-internal-error",
      exampleFix: "File a bug against `@norkron/web-tooltip` with the `message` field (which embeds the caught error's message) and a reproducer DOM snippet."
    }
  });
} catch {
}
function autoUpdate(trigger, content, update) {
  if (typeof window === "undefined") return () => {
  };
  let firstSyncSwallowed = false;
  const wrapped = () => {
    if (!firstSyncSwallowed) {
      firstSyncSwallowed = true;
      return;
    }
    update();
  };
  const refLike = trigger ?? {
    getBoundingClientRect: () => ({ left: 0, top: 0, right: 0, bottom: 0, width: 0, height: 0, x: 0, y: 0, toJSON() {
      return this;
    } })
  };
  const floatStub = content ?? {
    nodeType: 1,
    getBoundingClientRect: () => ({ left: 0, top: 0, right: 0, bottom: 0, width: 0, height: 0, x: 0, y: 0, toJSON() {
      return this;
    } })
  };
  return autoUpdate$1(refLike, floatStub, wrapped);
}
var VERTICAL_SIDES = ["top", "bottom"];
var HORIZONTAL_SIDES = ["left", "right"];
function sidesForAxis(axis) {
  if (axis === "vertical") return VERTICAL_SIDES;
  if (axis === "horizontal") return HORIZONTAL_SIDES;
  return void 0;
}
function computePosition(input) {
  const { trigger, content, viewport, preferredSide, preferredAlign, sideOffset, collisionPadding, arrowSize, allowedSides } = input;
  const referenceVirtual = {
    getBoundingClientRect: () => ({
      left: trigger.left,
      top: trigger.top,
      right: trigger.left + trigger.width,
      bottom: trigger.top + trigger.height,
      width: trigger.width,
      height: trigger.height,
      x: trigger.left,
      y: trigger.top,
      toJSON() {
        return this;
      }
    })
  };
  const floatingVirtual = {
    nodeType: 1,
    getBoundingClientRect: () => ({
      left: 0,
      top: 0,
      right: content.width,
      bottom: content.height,
      width: content.width,
      height: content.height,
      x: 0,
      y: 0,
      toJSON() {
        return this;
      }
    })
  };
  const viewportBoundary = {
    nodeType: 1,
    getBoundingClientRect: () => ({
      left: 0,
      top: 0,
      right: viewport.width,
      bottom: viewport.height,
      width: viewport.width,
      height: viewport.height,
      x: 0,
      y: 0,
      toJSON() {
        return this;
      }
    })
  };
  const r = computePosition$1(referenceVirtual, floatingVirtual, {
    placement: { side: preferredSide, align: preferredAlign },
    offset: sideOffset,
    collisionPadding,
    collisionBoundary: viewportBoundary,
    allowedSides
  });
  const clamped = { top: r.y, left: r.x };
  const side = r.side;
  const result = {
    top: clamped.top,
    left: clamped.left,
    side,
    align: preferredAlign,
    flipped: r.flipped
  };
  if (typeof arrowSize === "number" && arrowSize > 0) {
    result.arrow = computeArrowPlacement(side, trigger, content, clamped, arrowSize);
  }
  return result;
}
function clampArrowCentre(desired, extent, arrowSize) {
  const min = 2 * arrowSize;
  const max = extent - 2 * arrowSize;
  if (max <= min) return extent / 2;
  return Math.max(min, Math.min(desired, max));
}
function computeArrowPlacement(side, trigger, content, clamped, arrowSize) {
  if (side === "top" || side === "bottom") {
    const triggerCenterX = trigger.left + trigger.width / 2;
    const desiredLeft = triggerCenterX - clamped.left;
    const left2 = clampArrowCentre(desiredLeft, content.width, arrowSize);
    const top2 = side === "top" ? content.height : 0;
    return { top: top2, left: left2 };
  }
  const triggerCenterY = trigger.top + trigger.height / 2;
  const desiredTop = triggerCenterY - clamped.top;
  const top = clampArrowCentre(desiredTop, content.height, arrowSize);
  const left = side === "left" ? content.width : 0;
  return { top, left };
}
var TooltipArrowContext = createContext(null);
function TooltipArrow({ size, className, style }) {
  const ctx = useContext(TooltipArrowContext);
  if (!ctx) return null;
  const { side, arrow, arrowSize: ctxSize } = ctx;
  const resolvedSize = size ?? ctxSize;
  const measured = arrow !== null;
  const finalStyle = {
    position: "absolute",
    // Drive sizing via custom property so consumer CSS overrides land.
    ["--nk-tooltip-arrow-size"]: `${resolvedSize}px`,
    width: `${resolvedSize * 2}px`,
    height: `${resolvedSize * 2}px`,
    // Offset by -arrowSize so `{top,left}` describes the arrow CENTRE.
    top: measured ? `${(arrow?.top ?? 0) - resolvedSize}px` : "0px",
    left: measured ? `${(arrow?.left ?? 0) - resolvedSize}px` : "0px",
    visibility: measured ? "visible" : "hidden",
    pointerEvents: "none",
    ...style
  };
  return /* @__PURE__ */ jsx(
    "span",
    {
      className: className ?? "nk-tooltip-arrow",
      "data-side": side,
      "data-testid": "nk-tooltip-arrow",
      "aria-hidden": "true",
      style: finalStyle
    }
  );
}

// src/trigger-marker.ts
var TOOLTIP_TRIGGER_ATTR = "data-norkron-tooltip-trigger";
var SelfCheckOptionsSchema = z.object({
  budgetMs: z.number().int().positive().max(5e3).optional(),
  minSeverity: z.enum(["critical", "major", "minor", "info"]).optional(),
  locale: z.string().min(2).max(35).optional()
}).strict();

// src/validate-self-check.ts
var DEFAULT_BUDGET_MS = 50;
function parseSelfCheckOptions(opts) {
  const parsed = SelfCheckOptionsSchema.safeParse(opts ?? {});
  if (parsed.success) {
    return {
      budgetMs: parsed.data.budgetMs ?? DEFAULT_BUDGET_MS,
      minSeverity: parsed.data.minSeverity ?? "info"
    };
  }
  return { budgetMs: DEFAULT_BUDGET_MS, minSeverity: "info" };
}

// src/self-check.ts
var STR_TO_LOCAL_CATEGORY = {
  theme: "theme",
  stylesheet: "stylesheet",
  a11y: "a11y",
  layout: "layout",
  rendering: "layout",
  content: "i18n",
  i18n: "i18n",
  cardinality: "cardinality",
  identity: "identity",
  schema: "schema",
  flow: "flow",
  deprecation: "deprecation",
  perf: "perf"
};
function adaptStructural(libId, scope, checks) {
  const out = [];
  for (const fn of checks) {
    try {
      for (const f of fn(scope)) {
        out.push({
          libId,
          code: f.code,
          category: STR_TO_LOCAL_CATEGORY[f.category] ?? "layout",
          severity: f.severity,
          message: f.message,
          selector: f.selector,
          suggestedFix: f.suggestedFix,
          extras: f.extras
        });
      }
    } catch {
    }
  }
  return out;
}
var LIB_ID = "@norkron/web-tooltip";
var SEVERITY_RANK = {
  info: 0,
  minor: 1,
  warning: 2,
  major: 3,
  error: 4,
  critical: 5
};
function meetsFloor(finding, floor) {
  return SEVERITY_RANK[finding.severity] >= SEVERITY_RANK[floor];
}
function selectorFor(el) {
  const testid = el.getAttribute("data-testid");
  if (testid) return `[data-testid="${testid}"]`;
  const tag = el.tagName.toLowerCase();
  const cls = el.getAttribute("class")?.split(/\s+/).filter(Boolean).slice(0, 2).join(".");
  return cls ? `${tag}.${cls}` : tag;
}
function isFocusable(el) {
  const tag = el.tagName.toLowerCase();
  const role = el.getAttribute("role");
  const tabindex = el.getAttribute("tabindex");
  if (tag === "button" || tag === "a" || tag === "input" || tag === "select" || tag === "textarea") {
    if (tabindex !== null && Number(tabindex) < 0) return false;
    return true;
  }
  if (tabindex !== null) {
    const n = Number(tabindex);
    if (!Number.isNaN(n) && n >= 0) return true;
  }
  if (role === "button" && tabindex !== null && Number(tabindex) >= 0) return true;
  return false;
}
function findHeadingAncestor(el) {
  let cur = el.parentElement;
  while (cur) {
    const tag = cur.tagName.toLowerCase();
    if (tag === "h1" || tag === "h2" || tag === "h3" || tag === "h4" || tag === "h5" || tag === "h6") {
      return cur;
    }
    if (cur.getAttribute("role") === "heading") return cur;
    cur = cur.parentElement;
  }
  return null;
}
function selfCheck(root, opts) {
  const start = typeof performance !== "undefined" && typeof performance.now === "function" ? performance.now() : Date.now();
  const safeOpts = parseSelfCheckOptions(opts);
  const findings = [];
  try {
    const triggerSet = /* @__PURE__ */ new Set();
    for (const el of root.querySelectorAll(`[${TOOLTIP_TRIGGER_ATTR}]`)) {
      triggerSet.add(el);
    }
    for (const el of root.querySelectorAll(
      '[data-norkron-lib^="@norkron/web-tooltip"]'
    )) {
      triggerSet.add(el);
    }
    const triggers = Array.from(triggerSet);
    if (triggers.length === 0) return findings.filter((f) => meetsFloor(f, safeOpts.minSeverity));
    for (const trigger of triggers) {
      const now = typeof performance !== "undefined" && typeof performance.now === "function" ? performance.now() : Date.now();
      if (now - start > safeOpts.budgetMs) {
        findings.push({
          libId: LIB_ID,
          code: "TT_SELFCHECK_BUDGET_EXCEEDED",
          category: "perf",
          severity: "info",
          message: `selfCheck exceeded budget ${safeOpts.budgetMs}ms; partial results returned.`,
          extras: { budgetMs: safeOpts.budgetMs, elapsed: now - start }
        });
        break;
      }
      if (!isFocusable(trigger)) {
        findings.push({
          libId: LIB_ID,
          code: "TT_TRIGGER_NOT_FOCUSABLE",
          category: "a11y",
          severity: "critical",
          message: "Tooltip trigger element lacks both a focusable role (button/a/input) and a non-negative tabindex. Keyboard users cannot trigger the tooltip.",
          selector: selectorFor(trigger),
          suggestedFix: 'Use a native <button> as the trigger, or add tabindex="0" + role="button" + a key handler.'
        });
      }
      const heading = findHeadingAncestor(trigger);
      if (heading) {
        findings.push({
          libId: LIB_ID,
          code: "TT_ACCNAME_POLLUTION",
          category: "a11y",
          severity: "major",
          message: `Tooltip trigger is nested inside a <${heading.tagName.toLowerCase()}> heading; the trigger's accessible name pollutes the heading's accname computation.`,
          selector: selectorFor(trigger),
          suggestedFix: "Move the tooltip trigger outside the heading element; if the trigger MUST sit near the heading, place it as a sibling.",
          extras: { headingTag: heading.tagName.toLowerCase() }
        });
      }
    }
    for (const trigger of triggers) {
      const helperScope = trigger;
      findings.push(
        ...adaptStructural(LIB_ID, helperScope, [
          checkContrast,
          checkViewportOverflow,
          checkHeadingHierarchy,
          checkDuplicateVisibleText,
          checkTextClipped,
          checkInlineStyleOnLibStamp,
          checkUtilityClassOnLibStamp
        ])
      );
    }
  } catch (err) {
    findings.push({
      libId: LIB_ID,
      code: "TT_SELFCHECK_INTERNAL_ERROR",
      category: "identity",
      severity: "info",
      message: `selfCheck threw internally: ${err instanceof Error ? err.message : String(err)}`
    });
  }
  return findings.filter((f) => meetsFloor(f, safeOpts.minSeverity));
}
var RESPONSIVE_CONTRACT = {
  lib: "@norkron/web-tooltip",
  supportedBreakpoints: ["xs", "sm", "md", "lg", "xl", "2xl", "3xl"],
  breakpointBehavior: {
    // Runtime edge-flip positioning makes the tooltip viewport-aware
    // without per-tier layout branching. Honest baseline: 'full' at
    // every tier.
    xs: "full",
    sm: "full",
    md: "full",
    lg: "full",
    xl: "full",
    "2xl": "full",
    "3xl": "full"
  },
  containerQueries: [],
  rwdSelfCheckCodes: []
};
registerResponsiveContract(RESPONSIVE_CONTRACT.lib, RESPONSIVE_CONTRACT);
var TooltipProviderContext = createContext(null);
var TooltipProvider = memo(function TooltipProvider2({
  children,
  delayDuration = 700,
  skipDelayDuration = 300,
  disableHoverableContent = false,
  axis = "both",
  sideOffset = 8
}) {
  const openCountRef = useRef(0);
  const lastCloseAtRef = useRef(0);
  const onOpen = useCallback(() => {
    openCountRef.current += 1;
  }, []);
  const onClose = useCallback(() => {
    openCountRef.current = Math.max(0, openCountRef.current - 1);
    lastCloseAtRef.current = Date.now();
  }, []);
  const isOpenInGroup = useCallback(() => openCountRef.current > 0, []);
  const wasRecentlyOpen = useCallback(() => {
    if (openCountRef.current > 0) return true;
    if (lastCloseAtRef.current === 0) return false;
    return Date.now() - lastCloseAtRef.current <= skipDelayDuration;
  }, [skipDelayDuration]);
  const value = useMemo(
    () => ({
      delayDuration,
      skipDelayDuration,
      disableHoverableContent,
      axis,
      sideOffset,
      isOpenInGroup,
      wasRecentlyOpen,
      onOpen,
      onClose
    }),
    [
      delayDuration,
      skipDelayDuration,
      disableHoverableContent,
      axis,
      sideOffset,
      isOpenInGroup,
      wasRecentlyOpen,
      onOpen,
      onClose
    ]
  );
  return /* @__PURE__ */ jsx(TooltipProviderContext.Provider, { value, children });
});
function useTooltipProviderContext() {
  return useContext(TooltipProviderContext);
}

// src/use-tooltip.ts
var DEFAULT_ARROW_SIZE = 8;
var FALLBACK_VIEWPORT = { width: 1024, height: 768 };
function getViewport() {
  if (typeof window === "undefined") return FALLBACK_VIEWPORT;
  return { width: window.innerWidth, height: window.innerHeight };
}
function rectFromDom(el) {
  if (!el) return { top: 0, left: 0, width: 0, height: 0 };
  const r = el.getBoundingClientRect();
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}
function useTooltip(opts = {}) {
  const {
    side: preferredSide = "top",
    align: preferredAlign = "center",
    delayMs,
    closeDelayMs,
    openDelay,
    closeDelay,
    sideOffset,
    collisionPadding = 12,
    axis,
    defaultOpen = false,
    open: controlled,
    onOpenChange,
    disabled = false,
    arrowSize = DEFAULT_ARROW_SIZE,
    autoUpdate: autoUpdateEnabled = true,
    disableHoverableContent,
    followCursor = false,
    skipDelay = true,
    exitDelay = 0
  } = opts;
  const providerCtx = useTooltipProviderContext();
  const resolvedOpenDelay = openDelay ?? delayMs ?? providerCtx?.delayDuration ?? 350;
  const resolvedCloseDelay = closeDelay ?? closeDelayMs ?? 100;
  const resolvedDisableHoverable = disableHoverableContent ?? providerCtx?.disableHoverableContent ?? false;
  const resolvedSideOffset = resolveLengthPx(sideOffset ?? providerCtx?.sideOffset ?? 8, 8);
  const resolvedAxis = axis ?? providerCtx?.axis ?? "both";
  const allowedSides = useMemo(() => sidesForAxis(resolvedAxis), [resolvedAxis]);
  const followCursorEnabled = followCursor !== false && followCursor != null;
  const followCursorAxis = typeof followCursor === "object" && followCursor != null ? followCursor.axis ?? "both" : "both";
  const reactId = useId();
  const tooltipId = `tt-${reactId.replace(/:/g, "")}`;
  const reasonRef = useRef(void 0);
  const onOpenChangeRef = useRef(onOpenChange);
  onOpenChangeRef.current = onOpenChange;
  const onChangeAdapter = useCallback((next) => {
    onOpenChangeRef.current?.(next, reasonRef.current);
  }, []);
  const { value: open, setValue: setOpenRaw } = useControlledState(
    controlled,
    defaultOpen,
    onChangeAdapter
  );
  const [position, setPosition] = useState({
    top: 0,
    left: 0,
    side: preferredSide,
    align: preferredAlign,
    flipped: false
  });
  const cursorRectRef = useRef(null);
  const [cursorTick, setCursorTick] = useState(0);
  const triggerRef = useRef(null);
  const contentRef = useRef(null);
  const openTimerRef = useRef(null);
  const closeTimerRef = useRef(null);
  const [exiting, setExiting] = useState(false);
  const exitTimerRef = useRef(null);
  const wasOpenForExitRef = useRef(false);
  const setOpen = useCallback(
    (next, reason = "programmatic") => {
      reasonRef.current = reason;
      setOpenRaw(next);
    },
    [setOpenRaw]
  );
  const clearTimers = useCallback(() => {
    if (openTimerRef.current) {
      clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);
  const showWithReason = useCallback(
    (reason) => {
      if (disabled) return;
      clearTimers();
      const warm = skipDelay ? providerCtx?.wasRecentlyOpen() ?? false : false;
      if (resolvedOpenDelay <= 0 || warm) {
        setOpen(true, reason);
        return;
      }
      openTimerRef.current = setTimeout(() => setOpen(true, reason), resolvedOpenDelay);
    },
    [clearTimers, disabled, resolvedOpenDelay, setOpen, providerCtx, skipDelay]
  );
  const hideWithReason = useCallback(
    (reason) => {
      clearTimers();
      if (resolvedCloseDelay <= 0) {
        setOpen(false, reason);
        return;
      }
      closeTimerRef.current = setTimeout(() => setOpen(false, reason), resolvedCloseDelay);
    },
    [clearTimers, resolvedCloseDelay, setOpen]
  );
  const show = useCallback(() => showWithReason("hover"), [showWithReason]);
  const hide = useCallback(() => hideWithReason("hover"), [hideWithReason]);
  const showFocus = useCallback(() => showWithReason("focus"), [showWithReason]);
  const hideFocus = useCallback(() => hideWithReason("focus"), [hideWithReason]);
  useEffect(() => {
    if (!open) return;
    const update = () => {
      const trig = followCursorEnabled && cursorRectRef.current ? cursorRectRef.current : rectFromDom(triggerRef.current);
      const cont = rectFromDom(contentRef.current);
      const vp = getViewport();
      setPosition(
        computePosition({
          trigger: trig,
          content: cont,
          viewport: vp,
          preferredSide,
          preferredAlign,
          sideOffset: resolvedSideOffset,
          collisionPadding,
          arrowSize,
          allowedSides
        })
      );
    };
    update();
    if (!autoUpdateEnabled) return;
    return autoUpdate(triggerRef.current, contentRef.current, update);
  }, [
    open,
    preferredSide,
    preferredAlign,
    resolvedSideOffset,
    allowedSides,
    collisionPadding,
    arrowSize,
    autoUpdateEnabled,
    followCursorEnabled,
    cursorTick
  ]);
  const dismissRefs = useMemo(() => [triggerRef, contentRef], []);
  useDismiss({
    enabled: open,
    refs: dismissRefs,
    onDismiss: (reason) => {
      clearTimers();
      setOpen(false, reason === "escape-key" ? "escape" : "pointer-outside");
      if (reason === "escape-key") {
        safeFocus(triggerRef.current);
      }
    }
  });
  useEffect(() => {
    if (open) {
      wasOpenForExitRef.current = true;
      if (exitTimerRef.current) {
        clearTimeout(exitTimerRef.current);
        exitTimerRef.current = null;
      }
      setExiting(false);
      return;
    }
    const wasOpen = wasOpenForExitRef.current;
    wasOpenForExitRef.current = false;
    if (!wasOpen || exitDelay <= 0) {
      setExiting(false);
      return;
    }
    setExiting(true);
    exitTimerRef.current = setTimeout(() => {
      exitTimerRef.current = null;
      setExiting(false);
    }, exitDelay);
  }, [open, exitDelay]);
  useEffect(
    () => () => {
      clearTimers();
      if (exitTimerRef.current) {
        clearTimeout(exitTimerRef.current);
        exitTimerRef.current = null;
      }
    },
    [clearTimers]
  );
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (!providerCtx) {
      wasOpenRef.current = open;
      return;
    }
    if (open && !wasOpenRef.current) providerCtx.onOpen();
    else if (!open && wasOpenRef.current) providerCtx.onClose();
    wasOpenRef.current = open;
  }, [open, providerCtx]);
  useEffect(() => {
    if (!followCursorEnabled) return;
    const triggerEl = triggerRef.current;
    if (!triggerEl) return;
    let rafId = 0;
    const onMove = (ev) => {
      if (rafId !== 0) return;
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        const prev = cursorRectRef.current;
        const x = followCursorAxis === "y" && prev ? prev.left : ev.clientX;
        const y = followCursorAxis === "x" && prev ? prev.top : ev.clientY;
        cursorRectRef.current = { top: y, left: x, width: 1, height: 1 };
        setCursorTick((t) => t + 1);
      });
    };
    triggerEl.addEventListener("pointermove", onMove);
    return () => {
      triggerEl.removeEventListener("pointermove", onMove);
      if (rafId !== 0) cancelAnimationFrame(rafId);
    };
  }, [followCursorEnabled, followCursorAxis, open]);
  const triggerProps = {
    // Gate on `open`: when the content is unmounted the id it points
    // at does not exist in the DOM (STR_ARIA_REFERENCE_BROKEN). React
    // drops the attribute when the value is `undefined`, so SR users
    // hear the trigger's accessible name while closed and pick up the
    // rich description as soon as content mounts (open transition).
    "aria-describedby": open ? tooltipId : void 0,
    onMouseEnter: show,
    onMouseLeave: hide,
    onFocus: showFocus,
    onBlur: hideFocus,
    ref: triggerRef
  };
  const getContentProps = useCallback(
    (extra) => {
      const base = {
        id: tooltipId,
        role: "tooltip",
        ref: contentRef,
        "data-side": position.side,
        "data-align": position.align,
        style: {
          position: "fixed",
          top: position.top,
          left: position.left,
          maxWidth: extra?.maxWidth,
          zIndex: extra?.zIndex ?? 1e3
        }
      };
      if (resolvedDisableHoverable) return base;
      return {
        ...base,
        onMouseEnter: () => {
          if (closeTimerRef.current) {
            clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
          }
        },
        onMouseLeave: hide
      };
    },
    [tooltipId, position.side, position.align, position.top, position.left, hide, resolvedDisableHoverable]
  );
  return {
    open,
    mounted: open || exiting,
    tooltipId,
    side: position.side,
    align: position.align,
    flipped: position.flipped,
    arrow: position.arrow ?? null,
    arrowSize,
    triggerRef,
    contentRef,
    triggerProps,
    getContentProps,
    show: () => {
      clearTimers();
      setOpen(true, "programmatic");
    },
    hide: () => {
      clearTimers();
      setOpen(false, "programmatic");
    }
  };
}

// src/version.ts
var LIB_VERSION = "0.10.0";
try {
  registerLazy(
    "@norkron/web-tooltip",
    () => selfCheck
  );
} catch {
}
function mergeRefs(...refs) {
  return (node) => {
    for (const r of refs) {
      if (!r) continue;
      if (typeof r === "function") r(node);
      else r.current = node;
    }
  };
}
function chain(...fns) {
  return (e) => {
    for (const fn of fns) if (fn) fn(e);
  };
}
var REACT_FORWARD_REF = /* @__PURE__ */ Symbol.for("react.forward_ref");
var REACT_MEMO = /* @__PURE__ */ Symbol.for("react.memo");
var REACT_LAZY = /* @__PURE__ */ Symbol.for("react.lazy");
function triggerAcceptsRef(type) {
  if (typeof type === "string") return true;
  if (type == null || typeof type !== "object") return false;
  const exotic = type;
  if (exotic.$$typeof === REACT_FORWARD_REF) return true;
  if (exotic.$$typeof === REACT_MEMO && exotic.type != null) {
    return triggerAcceptsRef(exotic.type);
  }
  if (exotic.$$typeof === REACT_LAZY) return false;
  return false;
}
var RefProbe = forwardRef(
  function RefProbe2({ children, refTarget }, outerRef) {
    const setSpan = (node) => {
      refTarget(node);
      if (typeof outerRef === "function") outerRef(node);
      else if (outerRef) outerRef.current = node;
    };
    return /* @__PURE__ */ jsx("span", { ref: setSpan, style: { display: "contents" }, children });
  }
);
var Tooltip = memo(function Tooltip2({
  trigger,
  content,
  contentClassName,
  maxWidth = 320,
  zIndex,
  portalContainer,
  testId,
  ...options
}) {
  const tt = useTooltip(options);
  const tooltipBg = useThemeVar("--tooltip-bg", "rgba(15, 23, 42, 0.95)");
  const tooltipFg = useThemeVar("--tooltip-fg", "#f8fafc");
  const tooltipBorder = useThemeVar("--tooltip-border", "rgba(148, 163, 184, 0.25)");
  useFocusTrap(tt.contentRef, tt.open);
  if (!isValidElement(trigger)) {
    throw new Error("@norkron/tooltip: `trigger` must be a single React element.");
  }
  const triggerEl = trigger;
  const existing = triggerEl.props ?? {};
  const canRef = triggerAcceptsRef(triggerEl.type);
  const merged = {
    // Only emit `aria-describedby` while the floating content is
    // actually mounted — per STR_ARIA_REFERENCE_BROKEN, referencing an
    // id that does not exist in the DOM is a silent a11y defect (SR
    // users hear nothing or fall back to the trigger's textContent).
    // React drops the attribute when the value is `undefined`.
    "aria-describedby": tt.open ? tt.tooltipId : void 0,
    onMouseEnter: chain(existing.onMouseEnter, tt.triggerProps.onMouseEnter),
    onMouseLeave: chain(existing.onMouseLeave, tt.triggerProps.onMouseLeave),
    onFocus: chain(existing.onFocus, tt.triggerProps.onFocus),
    onBlur: chain(existing.onBlur, tt.triggerProps.onBlur),
    // React 19: `ref` is a regular prop on elements; reading
    // `element.ref` was removed. Pull the user's ref out of props
    // instead of the deprecated element-level field.
    ...canRef ? { ref: mergeRefs(existing.ref, tt.triggerProps.ref) } : {}
  };
  const triggerMarker = {
    [TOOLTIP_TRIGGER_ATTR]: `@norkron/web-tooltip@${LIB_VERSION}`
  };
  const innerCloned = cloneElement(triggerEl, {
    ...merged,
    ...triggerMarker
  });
  const triggerRef = tt.triggerProps.ref;
  const clonedTrigger = canRef ? innerCloned : /* @__PURE__ */ jsx(
    RefProbe,
    {
      refTarget: (node) => {
        const target = node?.firstElementChild;
        triggerRef.current = target instanceof HTMLElement ? target : node ?? null;
      },
      children: innerCloned
    }
  );
  const contentProps = tt.getContentProps({ maxWidth, zIndex });
  const portalTarget = portalContainer ?? (typeof document !== "undefined" ? document.body : null);
  if (tt.open && !portalTarget) {
    try {
      warn({
        fn: "Tooltip",
        code: "TOOLTIP_PORTAL_FAILURE",
        message: "createPortal target unavailable; tooltip will silently no-render. Pass `portalContainer` or ensure document.body exists.",
        prop: "portalContainer",
        received: portalContainer,
        expected: "an HTMLElement (e.g. document.body)",
        dedupKey: "Tooltip:portal-failure"
      });
    } catch {
    }
  }
  const themeStyle = {
    // Inline overrides so the value is reactive without consumers needing
    // to ship CSS for these vars; explicit CSS still wins via specificity.
    "--tooltip-bg": tooltipBg,
    "--tooltip-fg": tooltipFg,
    "--tooltip-border": tooltipBorder,
    ...contentProps.style ?? {}
  };
  const tooltipNode = /* @__PURE__ */ jsx(TooltipArrowContext.Provider, { value: { side: tt.side, arrow: tt.arrow, arrowSize: tt.arrowSize }, children: /* @__PURE__ */ jsx(
    "div",
    {
      ...contentProps,
      className: contentClassName ?? "nk-tooltip-content",
      "data-state": tt.open ? "open" : "closed",
      "aria-hidden": tt.open ? void 0 : true,
      "data-testid": testId ?? "nk-tooltip-content",
      "data-flipped": tt.flipped ? "true" : void 0,
      "data-keep-clear": "true",
      "data-allow-edge-text": "true",
      style: themeStyle,
      children: /* @__PURE__ */ jsx(ErrorBoundary, { fallback: null, children: content })
    }
  ) });
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    clonedTrigger,
    tt.mounted && portalTarget ? /* @__PURE__ */ jsx(Portal, { container: portalTarget, children: tooltipNode }) : null
  ] });
});
var RichTooltip = memo(function RichTooltip2(props) {
  const openDelay = props.openDelay ?? props.delayMs ?? 700;
  const closeDelay = props.closeDelay ?? props.closeDelayMs ?? 300;
  const contentClassName = props.contentClassName ?? "nk-tooltip-content nk-tooltip-content--rich";
  const { hoverableContent = true, disableHoverableContent, ...rest } = props;
  const resolvedDisableHoverable = disableHoverableContent ?? !hoverableContent;
  return /* @__PURE__ */ jsx(
    Tooltip,
    {
      ...rest,
      openDelay,
      closeDelay,
      disableHoverableContent: resolvedDisableHoverable,
      contentClassName,
      content: /* @__PURE__ */ jsx("div", { "aria-live": "polite", children: props.content })
    }
  );
});
var TooltipTitle = memo(function TooltipTitle2({ children, className }) {
  return /* @__PURE__ */ jsx("strong", { className: className ?? "nk-tooltip-content__title", role: "heading", "aria-level": 3, children });
});
var TooltipDescription = memo(function TooltipDescription2({ children, className }) {
  return /* @__PURE__ */ jsx("p", { className: className ?? "nk-tooltip-content__description", children });
});
var TooltipFooter = memo(function TooltipFooter2({ children, className }) {
  return /* @__PURE__ */ jsx("div", { className: className ?? "nk-tooltip-content__footer", children });
});
var TooltipBadge = memo(function TooltipBadge2({
  side = "top",
  align = "start",
  delayMs = 200,
  testId,
  ...rest
}) {
  return /* @__PURE__ */ jsx(
    RichTooltip,
    {
      ...rest,
      side,
      align,
      delayMs,
      testId: testId ?? "tooltip-badge__content"
    }
  );
});

export { DEFAULT_ARROW_SIZE, LIB_VERSION, RESPONSIVE_CONTRACT, RichTooltip, TOOLTIP_TRIGGER_ATTR, Tooltip, TooltipArrow, TooltipBadge, TooltipDescription, TooltipFooter, TooltipProvider, TooltipTitle, autoUpdate, computePosition, selfCheck, sidesForAxis, useTooltip, useTooltipProviderContext };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map